/*
  Applying a discount code from the cart page.

  There is no storefront API for this -- the cart object is read-only and
  codes belong to checkout -- so the only supported route is to navigate to
  /discount/{code}, which puts the code on the session, and let it redirect
  back. That means a page load; nothing here can avoid one.

  Feedback after the reload is worked out rather than reported. Shopify tells
  us nothing about whether the code was accepted, so the redirect carries the
  attempted code back in the query string and this compares it against the
  discounts the cart is actually showing.
*/
class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.cart-discount__input');
    this.button = this.querySelector('[data-apply]');
    this.message = this.querySelector('[data-message]');

    this.button.addEventListener('click', () => this.apply());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.apply();
      }
    });

    this.reportOutcome();
  }

  apply() {
    const code = this.input.value.trim().toUpperCase();
    if (!code) {
      this.input.focus();
      return;
    }

    this.button.disabled = true;
    this.button.textContent = '…';

    // Where to land afterwards, carrying the attempt so the reloaded page can
    // say whether it worked.
    const back = `${window.location.pathname}?code=${encodeURIComponent(code)}`;
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(back)}`;
  }

  reportOutcome() {
    const attempted = new URLSearchParams(window.location.search).get('code');
    if (!attempted) return;

    /*
      Shopify reports nothing, so the outcome is read off the discounts the
      cart is now showing -- and it has to be THIS code that is showing, not
      merely some discount. Testing for any discount line at all reported a
      junk code as applied whenever a real one was already on the cart, which
      is exactly what happened the first time this was tried.

      Only cart-level discounts surface here, which is why a code that was
      accepted but applies to shipping gets the softer wording rather than
      being called invalid.
    */
    const titles = [...document.querySelectorAll('.discounts__discount')].map((el) =>
      el.textContent.trim().toUpperCase()
    );
    const thisOne = titles.some((t) => t.includes(attempted.toUpperCase()));

    if (thisOne) {
      this.say(`${attempted} applied.`, 'ok');
    } else {
      this.say(`${attempted} was not applied to the cart. If it is a shipping offer it will show at checkout.`, 'bad');
    }

    // Take the parameter back out so a refresh does not repeat the message.
    const clean = new URL(window.location.href);
    clean.searchParams.delete('code');
    window.history.replaceState({}, '', clean);
  }

  say(text, kind) {
    this.message.textContent = text;
    this.message.classList.add('cart-discount__message--shown', `cart-discount__message--${kind}`);
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
